import React, { useState, useEffect } from 'react';
import { FaEye } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { Paragraph } from '../../basic/title';
import { useDispatch } from 'react-redux';
import QuickViewModal from "./QuickViewModal";

const ProductsortpillsCard = ({ skeletonCount, allProducts, productIsLoading, productError }) => {
    const [loading, setLoading] = useState(true);
    const [selectedVariants, setSelectedVariants] = useState({});
    const [selectedVariantDetails, setSelectedVariantDetails] = useState({});
    const [showQuickView, setShowQuickView] = useState(false);
    const [selectedProductForQuickView, setSelectedProductForQuickView] = useState(null);

    const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    // ✅ Variant Handling for BOTH 'variant_combinations' & 'variant_value'
    useEffect(() => {
        const variants = {};
        const variantDetails = {};

        allProducts?.forEach(product => {
            if (product.is_variant === "false") {
                variantDetails[product.id] = {
                    price: product.price,
                    discount_price: product.discount_price,
                    stock_quantity: product.stock_quantity,
                    stock_status: product.stock_status,
                };
            } else {
                const allVariants = product.variant_combinations?.length
                    ? product.variant_combinations
                    : product.variant_value?.flatMap(v => v.multiple_values) || [];

                const firstInStock = allVariants.find(v =>
                    Number(v.stock_quantity) > 0 &&
                    (v.stock_status === "in_stock" || !v.stock_status)
                ) || allVariants[0];

                if (firstInStock) {
                    variants[product.id] = firstInStock.id;
                    variantDetails[product.id] = {
                        id: firstInStock.id,
                        value: firstInStock.value || '',
                        price: firstInStock.price || product.price || 0,
                        discount_price: firstInStock.discount_price || product.discount_price || 0,
                        stock_quantity: firstInStock.stock_quantity || 0,
                        stock_status: firstInStock.stock_status || 'in_stock'
                    };
                }
            }
        });

        setSelectedVariants(variants);
        setSelectedVariantDetails(variantDetails);

        console.log("✅ All Selected Variant Details (with price info)", variantDetails);
    }, [allProducts]);

    const handleQuickViewClick = (product) => {
        setSelectedProductForQuickView(product);
        setShowQuickView(true);
    };

    const handleCloseQuickView = () => {
        setShowQuickView(false);
        setSelectedProductForQuickView(null);
    };

    if (loading || productIsLoading) {
        return (
            <div className="flex flex-col space-y-4">
                {Array.from({ length: skeletonCount }, (_, index) => (
                    <div key={index} className="flex items-center p-4 border rounded-lg shadow-md bg-gray-100">
                        <div className="w-1/4 h-[150px] bg-gray-300 animate-pulse rounded-md" />
                        <div className="w-3/4 pl-2">
                            <div className="w-full h-4 bg-gray-300 animate-pulse rounded-md mb-2" />
                            <div className="w-full h-4 bg-gray-300 animate-pulse rounded-md mb-2" />
                            <div className="w-full h-4 bg-gray-300 animate-pulse rounded-md mb-2" />
                            <div className="w-1/2 h-4 bg-gray-300 animate-pulse rounded-md" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (productError) {
        return <div className="text-red-500">An error occurred: {productError}</div>;
    }

    return (
        <>
            {allProducts.map((item, index) => {
                const selected = selectedVariantDetails[item.id];

                console.log("🧾 Rendering Product", item);

                const isProductOutOfStock = item.is_variant === "false"
                    ? item.stock_quantity === 0 || item.stock_status === "out_of_stock"
                    : (selected ? selected.stock_quantity === 0 || selected.stock_status === "out_of_stock" : true);

                return (
                    <div
                        className={`grid grid-cols-12 sm:gap-4 gap-2 mb-2 sm:py-2 py-1 sm:px-2 px-1 items-center rounded-lg shadow-md ${index % 2 === 0 ? '' : 'bg-gray-200'}`}
                        key={item.id}
                    >
                        <div
                            className="lg:col-span-2 sm:col-span-4 col-span-4 flex items-center h-[150px] justify-center rounded-md bg-white cursor-pointer relative group"
                            onClick={() => navigate(`/shop/${item?.id}`)}
                        >
                            {item.additional_images?.length > 0 ? (
                                <img
                                    src={BASE_IMAGE_URL + item.additional_images[0]}
                                    alt='product_img'
                                    className="w-full h-full object-cover rounded-md"
                                />
                            ) : (
                                <div className="w-full h-full bg-gray-200 rounded-md" />
                            )}
                        </div>

                        <div className="lg:col-span-8 sm:col-span-6 col-span-6 flex flex-col justify-center">
                            <h2 className="text-[#00A762] text-left font-dm text-lg capitalize font-medium">
                                {item.name}
                            </h2>

                            <Paragraph title={item.short_description} shortDescription='true' textAlign='onyleft' readjustifytext='start' />

                            {selected?.discount_price ? (
                                <div className="flex items-center gap-2 text-[#00A762] font-dm text-lg font-medium pb-2">
                                    <span className="text-xs line-through">₹{selected?.price}</span>
                                    <span>₹{selected?.discount_price}</span>
                                    <span className="text-sm text-red-500">
                                        ({Math.round(((selected?.price - selected?.discount_price) / selected?.price) * 100)}% OFF)
                                    </span>
                                </div>
                            ) : (
                                <h2 className="text-[#00A762] font-dm text-lg font-medium pb-2">
                                    ₹{selected?.price}
                                </h2>
                            )}
                        </div>

                        <div className="lg:col-span-2 sm:col-span-2 col-span-2 flex flex-col items-end justify-center gap-2">
                            <button
                                onClick={() => handleQuickViewClick(item)}
                                className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#072320]"
                            >
                                <FaEye className='text-white text-[22px]' />
                            </button>
                        </div>
                    </div>
                );
            })}

            {showQuickView && selectedProductForQuickView && (
                <QuickViewModal
                    product={selectedProductForQuickView}
                    onClose={handleCloseQuickView}
                />
            )}
        </>
    );
};

export default ProductsortpillsCard;
